import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Heart, Shield, Users, Award } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'
import { SEO } from '@/components/SEO'

const values = [
  {
    icon: Heart,
    title: 'We Care About Your Business',
    description: 'We are not just building websites. We are helping real people grow real businesses. Your success matters to us personally.',
  },
  {
    icon: Shield,
    title: 'No Hidden Costs',
    description: 'We tell you exactly what things cost upfront. No surprise fees. No confusing invoices. What we quote is what you pay.',
  },
  {
    icon: Users,
    title: 'Real People, Real Support',
    description: 'When you call us, you talk to a real person. We are based in the UK and we speak plain English, not tech jargon.',
  },
  {
    icon: Award,
    title: 'We Stand Behind Our Work',
    description: 'If you are not happy, we make it right. Simple as that. We do not consider a project done until you love it.',
  },
]

export function About() {
  return (
    <>
      <SEO
        title="About Us - Meet the NEXAIGEN Team"
        description="NEXAIGEN helps UK small businesses succeed online. We believe every business deserves great digital presence, not just big companies. Learn about our mission and values."
        keywords={[
          'about NEXAIGEN',
          'UK web design agency',
          'small business web developers',
          'digital agency London',
          'web design company UK',
        ]}
        url="https://nexaigen.co.uk/about"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-32 pb-20 container bg-background min-h-screen"
      >
        {/* Hero */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center max-w-7xl mx-auto">
        <div>
          <div className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold mb-6">
            About Us
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-8 text-white leading-tight">
            We Help Small Businesses <br />
            <span className="gradient-text">Go Digital</span>
          </h1>
          <div className="space-y-6 text-slate-400 text-lg leading-relaxed">
            <p>
              <strong className="text-white">Hi, we are NEXAIGEN.</strong> We started this business because we saw too many small business owners struggling with confusing tech companies charging too much for too little.
            </p>
            <p>
              We believe every business deserves a great website and online presence, not just the big companies with big budgets. That is why we keep our prices fair and our explanations simple.
            </p>
            <p>
              Whether you run a bakery, a salon, a dental practice, or any other small business, we are here to help you reach more customers online.
            </p>
          </div>

          <div className="mt-10">
            <Link to="/contact">
              <Button size="lg">
                Let's Talk <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Image */}
        <div className="relative group">
          <div className="absolute -inset-4 bg-gradient-to-r from-primary to-secondary rounded-[2.5rem] opacity-20 group-hover:opacity-30 blur-2xl transition-opacity duration-700" />
          <div className="relative aspect-square rounded-[2rem] overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
              alt="NEXAIGEN Team"
              className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-primary/20 mix-blend-overlay" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-24 max-w-4xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 p-8 rounded-3xl bg-slate-900/50 border border-white/5">
          {siteConfig.stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-slate-400 font-semibold tracking-wider uppercase">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Values */}
      <div className="mt-32 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            What We <span className="text-primary">Believe In</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            These are not just words on a page. This is how we run our business every day.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-8 rounded-3xl bg-slate-900/50 border border-white/5 hover:border-primary/30 transition-colors"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6">
                <value.icon size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
              <p className="text-slate-400 leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Our Promise */}
      <div className="mt-32 max-w-4xl mx-auto">
        <div className="p-12 rounded-3xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Our Promise to You
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-8">
            We will always be honest about what you need (and what you don't).
            We will never try to sell you something that won't help your business.
            And we will always be here when you need us.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary">
              Get in Touch <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </div>
      </motion.div>
    </>
  )
}

export default About
